import { type FC, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useAbility } from "@/partner/shared/lib";
import {
  useCurrentPlatformQuery,
  useUpdateCurrentPlatformMutation,
} from "@/partner/entities/platforms";

/**
 * Блок «Пропажа видео в эфире» страницы настроек платформы
 * (PARTNER-PAGES.md, архетип B; спека 2026-10-24-stream-monitoring-ux,
 * «Уведомления»): письма участникам организации, если в идущем эфире две
 * минуты нет видео, и когда оно вернулось. По умолчанию включено.
 * Сохраняет только своё поле через updateCurrentPlatform, по образцу
 * ViewingSettingsCard.
 */
export const StreamSignalSettingsCard: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();
  const canUpdate = ability.can("update", "Platform");
  const { data } = useCurrentPlatformQuery();
  const platform = data?.currentPlatform;
  const saved = platform?.streamSignalAlertsEnabled ?? true;
  const [enabled, setEnabled] = useState<boolean>(saved);

  useEffect(() => {
    setEnabled(saved);
  }, [saved]);

  const [updateCurrentPlatform, { loading }] = useUpdateCurrentPlatformMutation(
    {
      refetchQueries: ["CurrentPlatform"],
      onCompleted: () =>
        enqueueSnackbar(t("streamSignalSettings.saved"), {
          variant: "success",
        }),
      onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    }
  );

  const isDirty = !!platform && enabled !== saved;

  const handleSave = () => {
    if (!canUpdate) return;
    void updateCurrentPlatform({
      variables: { input: { streamSignalAlertsEnabled: enabled } },
    });
  };

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("streamSignalSettings.title")}
        subheader={t("streamSignalSettings.subtitle")}
        sx={{ px: 2, pt: 2, pb: 0 }}
      />
      <CardContent sx={{ px: 2, pb: 2, pt: 2 }}>
        <Stack spacing={2}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  disabled={!canUpdate}
                />
              }
              label={t("streamSignalSettings.enabledLabel")}
            />
            <FormHelperText>
              {t("streamSignalSettings.enabledHelper")}
            </FormHelperText>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={loading || !isDirty || !canUpdate}
            >
              {t("common.actions.save")}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
